/* eslint-disable @typescript-eslint/restrict-template-expressions */
import type { GraphQLError } from 'graphql';
import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import type { ButtonProps } from '../components/Button';
import { UserType } from '../graphql/generated/graphql';
import getPlatformInfoFromMentionType from '../integrations/getPlatformInfoFromMentionType';
import parseGraphQLError from '../utils/error/parseGraphQLError';
import EDITOR_INTEGRATION_MENTIONS from './mentions/EditorIntegrationMentions';
import InstallIntegrationButton from './mentions/InstallIntegrationButton';
import ManageIntegrationButton from './mentions/ManageIntegrationButton';
import RefetchMentionButton from './mentions/RefetchMentionButton';

interface HandlerProps {
  title: string;
  message: string;
  ignorable?: boolean;
  primaryButton?: ReactElement | undefined;
}

interface Handler {
  adminOnly?: boolean;
  showOncePerSession?: boolean;
  getProps: (
    detail: { type: string; objectName: string },
    options: {
      refetch: () => void;
      buttonVariant: 'link' | 'secondary';
      buttonSize?: ButtonProps['size'];
    },
  ) => HandlerProps;
}

const tokenInvalid: Handler = {
  adminOnly: true,
  showOncePerSession: true,
  getProps: ({ type }, { buttonVariant, buttonSize }) => {
    const { platform, name } = getPlatformInfoFromMentionType(type);
    return {
      title: 'Lost access',
      message: `Slab has lost access to ${name}, usually due to permissions changing or revoked from ${name}'s side. Please reinstall the integration.`,
      ignorable: true,
      primaryButton:
        platform == null ? undefined : (
          <ManageIntegrationButton
            platform={platform}
            variant={buttonVariant}
            size={buttonSize}
          />
        ),
    };
  },
};

const getIntegrationErrorHandlers = (
  mode: 'mention' | 'embed',
): Record<string, Handler> => ({
  NOT_FOUND: {
    showOncePerSession: true,
    getProps: ({ type, objectName }) => {
      const { name } = getPlatformInfoFromMentionType(type);
      return {
        title: 'Is this link valid?',
        message: `We are unable to find a valid ${name} ${objectName} for the link you pasted. Please verify it is valid and that you have access to it.`,
      };
    },
  },
  INTEGRATION_NOT_ENABLED: {
    adminOnly: true,
    showOncePerSession: true,
    getProps: ({ type, objectName }, { buttonVariant, buttonSize }) => {
      const { platform, name, isPremium } =
        getPlatformInfoFromMentionType(type);

      return {
        title: `${name} integration available`,
        message: `Enabling the ${name} integration will turn the link you pasted into ${
          mode === 'mention' ? 'a mention' : 'an embed'
        }. Hovering over it will show detailed information about the ${name} ${objectName}.`,
        ignorable: true,
        primaryButton:
          platform == null ? undefined : (
            <InstallIntegrationButton
              platform={platform}
              isPremium={isPremium || false}
              variant={buttonVariant}
              size={buttonSize}
            />
          ),
      };
    },
  },
  MENTION_PARENT_DISALLOWED: {
    adminOnly: true,
    showOncePerSession: true,
    getProps: ({ type, objectName }, { buttonVariant, buttonSize }) => {
      const { platform, name, mention } = getPlatformInfoFromMentionType(type);
      const parentName = mention?.parentName;
      return {
        title: `Unsupported ${name} ${objectName}`,
        message: parentName
          ? `The ${name} link you pasted cannot be converted because the ${objectName} is a part of a ${parentName} that hasn't been enabled for your team.`
          : '',
        ignorable: true,
        primaryButton:
          platform == null ? undefined : (
            <ManageIntegrationButton
              platform={platform}
              variant={buttonVariant}
              size={buttonSize}
            />
          ),
      };
    },
  },

  TOKEN_INVALID: tokenInvalid,
  BAD_CREDENTIAL: tokenInvalid,

  API_ERROR: {
    getProps: ({ type }, { refetch, buttonVariant, buttonSize }) => {
      const { name } = getPlatformInfoFromMentionType(type);
      return {
        title: `We failed to connect with ${name}`,
        message: `The link you just pasted cannot be converted to ${
          mode === 'mention' ? 'a mention' : 'an embed'
        } because it isn't loading as expected.`,
        primaryButton: (
          <RefetchMentionButton
            refetch={refetch}
            variant={buttonVariant}
            size={buttonSize}
          />
        ),
      };
    },
  },
});

const getGraphQLErrorCode = (error: Error | undefined) => {
  if (!error) return null;
  const graphQLError = ((error as unknown as { graphQLErrors: GraphQLError[] })
    .graphQLErrors ?? [])[0];
  return graphQLError && parseGraphQLError(graphQLError).code;
};

const hasShownToastInSession: { [platform: string]: boolean } = {};

const useIntegrationErrorHandlerData = ({
  mode,
  type,
  view,
  refetch,
  error,
  buttonVariant = 'link',
  buttonSize,
}: {
  mode: 'mention' | 'embed';
  type: string;
  view: 'toast' | 'hoverCard';
  refetch: () => unknown;
  error?: Error;
  buttonVariant?: 'link' | 'secondary';
  buttonSize?: ButtonProps['size'];
}): {
  errorCode?: string | null;
  handlerProps?: HandlerProps;
  onShow?: () => void;
  platform?: string;
} => {
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );
  const hiddenPrompts = useAppSelector(
    (state) => state.ui.hiddenMentionIntegrationPrompts,
  );

  return useMemo(() => {
    const errorCode = getGraphQLErrorCode(error);
    if (!errorCode) {
      return {};
    }

    const handler = getIntegrationErrorHandlers(mode)[errorCode];
    const mentionInfo = EDITOR_INTEGRATION_MENTIONS[type];

    const { apiEnum } = mentionInfo || {};
    const platform = apiEnum
      ? getPlatformInfoFromMentionType(apiEnum).platform
      : null;

    if (
      !handler ||
      (handler.adminOnly && !isAdmin) ||
      !mentionInfo ||
      !platform ||
      (view === 'toast' &&
        (hiddenPrompts[platform] ||
          (handler.showOncePerSession && hasShownToastInSession[platform])))
    ) {
      return { errorCode, platform: platform ?? undefined };
    }

    const handlerProps = handler.getProps(mentionInfo, {
      refetch,
      buttonVariant,
      buttonSize,
    });

    const onShow = () => {
      if (handler.showOncePerSession) hasShownToastInSession[platform] = true;
    };

    return { errorCode, handlerProps, onShow, platform };
  }, [
    buttonVariant,
    buttonSize,
    error,
    hiddenPrompts,
    isAdmin,
    mode,
    refetch,
    type,
    view,
  ]);
};

export default useIntegrationErrorHandlerData;
